import type { CanvasVariant, VariantAxis } from "../canvas/variant-resolver";
import { variantsOfFinish, type Finish } from "../canvas/finish-options";
import { groupVariantsByRatio, type SizeGroup } from "../canvas/size-groups";
import { cheapestDisplayPrice, type DisplayPrice } from "../canvas/variant-price";
import type { OfferedSize } from "./artwork-sizing";

/**
 * What a work opens on when someone is about to buy it: the artwork page and
 * the gallery's quick-add sheet ask the same questions with the same pickers,
 * so the rules for the starting answer live here, free of React.
 */

/**
 * The sizes in the shop's standard finish.
 *
 * Every finish of a given size has the same area, so "the first size" would
 * land on whichever finish happened to sort first — and it was landing on
 * unmounted paper, quietly showing an 18 € print where the shop sells a 39 €
 * canvas. The definition's axis order is the shop's own statement of what is
 * standard: canvas before paper, stretched before rolled. Each axis takes the
 * first declared value this master can actually be sold as, given the axes
 * already decided.
 *
 * Smallest first, like `printable` (offeredSizes sorts it so).
 */
export function standardFinishSizes(
  printable: OfferedSize[],
  axes: VariantAxis[],
): OfferedSize[] {
  if (!printable.length) return [];
  const chosen: Record<string, string> = {};
  const fits = (p: OfferedSize) =>
    Object.entries(chosen).every(([k, v]) => p.variant.axisValues?.[k] === v);
  for (const axis of axes) {
    if (axis.key === "size") continue;
    const preferred = axis.values.find((value) =>
      printable.some((p) => p.variant.axisValues?.[axis.key] === value.value && fits(p)),
    );
    if (preferred) chosen[axis.key] = preferred.value;
  }
  const sizes = printable.filter(fits);
  return sizes.length ? sizes : printable;
}

/**
 * The shortest long side a print needs to count as the shop's entry size.
 *
 * A "from" price taken from the tiniest format made every card say the same
 * thing — a 20 × 20 or a 30 × 40 that hardly anybody hangs over a sofa.
 * Prices are quoted, and the quick-add sheet opens, at the smallest size
 * whose longer side is at least this; a work too small for any such size
 * falls back to its smallest one.
 */
export const ENTRY_LONG_SIDE_MM = 400;

const longSide = (p: OfferedSize) => Math.max(p.widthMm, p.heightMm);

/** The sizes from the entry size up (all of them when none is that big). Order kept. */
export function entrySized(sizes: OfferedSize[]): OfferedSize[] {
  const big = sizes.filter((p) => longSide(p) >= ENTRY_LONG_SIDE_MM);
  return big.length ? big : sizes;
}

/**
 * Where the quick-add sheet opens, and the size the card's room preview
 * hangs: the shop's standard finish at its entry size (see
 * ENTRY_LONG_SIDE_MM). One tap changes either.
 */
export function entryVariantId(
  printable: OfferedSize[],
  axes: VariantAxis[],
): string | null {
  return entrySized(standardFinishSizes(printable, axes))[0]?.variant.id ?? null;
}

/** One material a work can be printed on, and what it costs from. */
export interface MaterialOffer {
  /** The `material` axis value: "paper", "canvas". */
  material: string;
  /** The cheapest variant of this material at or above the entry size. */
  price: DisplayPrice;
}

/**
 * Each material this master can be printed on, with its "from" price, cheapest
 * first.
 *
 * Per material because the two are different things at different prices: a
 * single "od 9,90 €" told the customer neither that canvas exists nor what it
 * costs. Each price is the cheapest finish of that material (rolled, say) at
 * the entry size, in the market's currency, from the sizes this master can
 * honestly be printed at. Variants with no material are left out.
 */
export function materialOffers(
  printable: OfferedSize[],
  currency: string,
): MaterialOffer[] {
  const byMaterial = new Map<string, OfferedSize[]>();
  for (const p of printable) {
    const material = p.variant.axisValues?.material;
    if (!material) continue;
    byMaterial.set(material, [...(byMaterial.get(material) ?? []), p]);
  }
  const offers: MaterialOffer[] = [];
  for (const [material, sizes] of byMaterial) {
    const price = cheapestDisplayPrice(
      entrySized(sizes).map((p) => p.variant),
      currency,
    );
    if (price) offers.push({ material, price });
  }
  // Prices in one list share a currency unless a market lacks its own price
  // for some size; compare only like with like, else keep the first-seen order.
  return offers.sort((a, b) =>
    a.price.currency === b.price.currency ? a.price.amount - b.price.amount : 0,
  );
}

/** Centimetres, width × height as the work hangs. */
export interface SizeCm {
  width: number;
  height: number;
}

const toCm = (mm: number) => Math.round(mm / 10);

/**
 * The smallest and the largest size a work can be printed at, turned the way
 * it hangs. Null when there is none; `from` and `to` are equal when there is
 * only one.
 */
export function sizeRange(printable: OfferedSize[]): { from: SizeCm; to: SizeCm } | null {
  if (!printable.length) return null;
  const area = (p: OfferedSize) => p.widthMm * p.heightMm;
  const sorted = printable.slice().sort((a, b) => area(a) - area(b));
  const cm = (p: OfferedSize) => ({ width: toCm(p.widthMm), height: toCm(p.heightMm) });
  return { from: cm(sorted[0]), to: cm(sorted[sorted.length - 1]) };
}

/** What the shop prints on, read off the formats on sale. */
export interface CatalogueOptions {
  /** Per material, in the definition's order, the finishes it comes in. */
  materials: { material: string; surfaces: string[]; mounts: string[] }[];
  /** The smallest and largest format on sale, as listed (not turned). */
  sizes: { from: SizeCm; to: SizeCm } | null;
}

/**
 * The gallery's one-line explanation of the options, from the variants that
 * are actually on sale — so it can never promise a gloss canvas or a frame for
 * paper that the shop does not sell. Values are in the definition's declared
 * order; a value it does not declare trails behind.
 */
export function catalogueOptions(
  variants: CanvasVariant[] | null | undefined,
  axes: VariantAxis[],
): CatalogueOptions {
  const active = (variants ?? []).filter((v) => v.active !== false);
  const ordered = (key: string, found: Set<string>) => {
    const declared = axes.find((a) => a.key === key)?.values.map((x) => x.value) ?? [];
    return [
      ...declared.filter((v) => found.has(v)),
      ...[...found].filter((v) => !declared.includes(v)),
    ];
  };
  const materials = ordered(
    "material",
    new Set(active.map((v) => v.axisValues?.material).filter((m): m is string => !!m)),
  ).map((material) => {
    const of = active.filter((v) => v.axisValues?.material === material);
    const values = (key: string) =>
      ordered(key, new Set(of.map((v) => v.axisValues?.[key]).filter((x): x is string => !!x)));
    return { material, surfaces: values("surface"), mounts: values("mount") };
  });

  const area = (v: CanvasVariant) => (v.printSpec?.widthMm ?? 0) * (v.printSpec?.heightMm ?? 0);
  const sized = active.filter((v) => area(v) > 0).sort((a, b) => area(a) - area(b));
  const cm = (v: CanvasVariant) => ({ width: toCm(v.printSpec.widthMm), height: toCm(v.printSpec.heightMm) });
  return {
    materials,
    sizes: sized.length ? { from: cm(sized[0]), to: cm(sized[sized.length - 1]) } : null,
  };
}

/**
 * Every size of one finish as a single list, smallest first.
 *
 * The editor's "other shape" step is for a photo the customer may crop; here
 * every offered size already fits the painting (within the print tolerance),
 * so splitting 60 × 40 from 70 × 50 would ask a question with no wrong answer.
 */
export function artworkSizeGroups(
  printableVariants: CanvasVariant[],
  finish: Finish,
): SizeGroup[] {
  const sizes = groupVariantsByRatio(variantsOfFinish(printableVariants, finish))
    .flatMap((group) => group.variants)
    .sort(
      (a, b) =>
        a.printSpec.widthMm * a.printSpec.heightMm -
        b.printSpec.widthMm * b.printSpec.heightMm,
    );
  return sizes.length ? [{ ratio: "", variants: sizes }] : [];
}

/** The option axes, read off the definition each variant row was joined to. */
export function variantAxesOf(rows: CanvasVariant[] | null | undefined): VariantAxis[] {
  return ((rows?.[0] as { definition?: { variantAxes?: VariantAxis[] } } | undefined)
    ?.definition?.variantAxes ?? []) as VariantAxis[];
}
